import { Alert, Input, message, Tag } from 'antd';
import { ArrowRight, Boxes, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  TASK_ID_PATTERN,
  taskPlatformApi,
  taskPlatformErrorMessage,
} from '@/services/taskPlatformApi';
import type { WorkerPool } from '@/types/taskPlatform';
import { admissionTag, formatTaskTime } from './status';

type WorkerPoolRow = WorkerPool & { id: string };

export function TaskPlatformPage() {
  const navigate = useNavigate();
  const [taskId, setTaskId] = useState('');
  const [workerPools, setWorkerPools] = useState<WorkerPoolRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const reply = await taskPlatformApi.listWorkerPools();
      setWorkerPools(
        (reply.workerPools || []).map((pool) => ({
          ...pool,
          id: pool.workerPoolId,
        })),
      );
    } catch (error) {
      setLoadError(
        taskPlatformErrorMessage(error, 'Worker Pool 加载失败，请稍后重试'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const searchTask = useCallback(() => {
    const value = taskId.trim();
    if (!TASK_ID_PATTERN.test(value)) {
      message.warning('请输入完整的 taskId');
      return;
    }
    navigate(`/tasks/executions/${value}`);
  }, [navigate, taskId]);

  const columns = useMemo(
    () => [
      {
        id: 'taskType',
        header: 'Task Type',
        accessor: (row: WorkerPoolRow) => (
          <div>
            <div className="font-mono font-medium text-text-strong">
              {row.taskType}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {row.owner}
            </div>
          </div>
        ),
      },
      {
        id: 'scope',
        header: 'Worker Scope',
        accessor: (row: WorkerPoolRow) => (
          <div className="space-y-1">
            <div>{row.producer}</div>
            <div className="font-mono text-xs text-muted-foreground">
              {row.executionLane} / {row.laneGeneration}
            </div>
          </div>
        ),
      },
      {
        id: 'version',
        header: '版本',
        accessor: (row: WorkerPoolRow) => (
          <div className="space-x-1">
            <Tag>Definition {row.definitionVersion}</Tag>
            <Tag>Input {row.inputSchemaVersion}</Tag>
          </div>
        ),
      },
      {
        id: 'admission',
        header: '接单状态',
        accessor: (row: WorkerPoolRow) => admissionTag(row.admissionEnabled),
      },
      {
        id: 'publishedAt',
        header: '发布时间',
        accessor: (row: WorkerPoolRow) => formatTaskTime(row.publishedAt),
      },
      {
        id: 'actions',
        header: '操作',
        accessor: (row: WorkerPoolRow) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/tasks/workers/${row.workerPoolId}`)}
          >
            查看任务
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Boxes className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold text-text-strong">任务</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            按 Worker Pool 查看平台能力、接单状态和执行记录。
          </p>
        </div>
        <div className="w-full lg:w-[520px]">
          <Input.Search
            value={taskId}
            onChange={(event) => setTaskId(event.target.value)}
            onSearch={searchTask}
            enterButton="查询 Task ID"
            prefix={<Search className="h-4 w-4 text-muted-foreground" />}
            placeholder="输入完整 task_xxx"
          />
        </div>
      </div>

      <Alert
        type="info"
        showIcon
        message="接单状态为只读信息"
        description="接单中表示允许新任务进入该 Worker Pool，不代表 Worker 进程在线；已关闭也不会影响在途任务。"
      />

      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Worker Pool</CardTitle>
            <CardDescription>
              每个 Pool 由 producer、taskType、lane 和 generation 唯一确定。
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            刷新
          </Button>
        </CardHeader>
        <CardContent>
          {loadError ? (
            <Alert className="mb-4" type="error" showIcon message={loadError} />
          ) : null}
          <DataTable
            columns={columns}
            data={workerPools}
            loading={loading}
            emptyMessage="当前授权范围内没有已发布的 Worker Pool"
          />
        </CardContent>
      </Card>
    </div>
  );
}

export { TaskExecutionDetailPage } from './TaskExecutionDetailPage';
export { WorkerPoolDetailPage } from './WorkerPoolDetailPage';
